'use client';

import type { ActivatedDrawId, MarketplaceBalance, RetailerBalanceResult } from '@svenskaspelab/poolbetting/schema/marketplace';
import { type ReactNode, useContext } from 'react';
import { createCacheContext } from './CacheContext';
type WalletContext = MarketplaceBalance | RetailerBalanceResult | undefined;
export const walletCachedContext = createCacheContext<WalletContext>();
export type InjectedWalletProps = {
  contextWallets?: {
    balance: number;
    soldShares: number;
    sharePrice: number;
    name: string;
    activatedDrawId: ActivatedDrawId;
    maxCommission?: number;
  }[];
};
const getNormalizedState = (state: MarketplaceBalance | RetailerBalanceResult | undefined): InjectedWalletProps['contextWallets'] => {
  if (!state) {
    return undefined;
  }
  if ('activatedDraws' in state) {
    return state.activatedDraws.map(draw => {
      const {
        activatedDrawId,
        balance
      } = draw;
      const sharePrice = Number.parseInt(draw.SharePrice, 10);
      const soldShares = balance / sharePrice;
      return {
        balance,
        sharePrice,
        soldShares,
        name: draw.GroupName,
        activatedDrawId
      };
    });
  }
  if ('activatedDraw' in state && state.activatedDraw) {
    const {
      activatedDrawId,
      currentPlayableAmount,
      soldShares,
      sharePrice,
      name,
      maxCommission
    } = state.activatedDraw;
    return [{
      balance: Number.parseInt(currentPlayableAmount, 10),
      sharePrice: Number.parseInt(sharePrice, 10),
      soldShares,
      name,
      activatedDrawId,
      maxCommission
    }];
  }
  return undefined;
};

/**
 * Higher-order component to inject wallet context into a wrapped component.
 *
 * @param WrappedComponent - The component to wrap and inject the wallet context into.
 * @returns A new component that renders the WrappedComponent with the wallet context injected as props.
 */
export function withContextWallet<TProps extends object>(WrappedComponent: React.ComponentType<TProps & InjectedWalletProps>) {
  return function ComponentWithWallet(props: TProps): ReactNode {
    const {
      context
    } = walletCachedContext;
    const walletContext = useContext(context);
    if (!walletContext) {
      return <WrappedComponent {...props} contextWallets={undefined} />;
    }
    const {
      state
    } = walletContext;
    const normalizedState = getNormalizedState(state);
    return <WrappedComponent {...props} contextWallets={normalizedState} />;
  };
}